import { Button, Dialog, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { subYears } from "date-fns";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { getLastDateOfReportingPeriod } from "@entrilia/shared/components/dataCollection/dataCollectionUtils";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../../api/adminApi";
import type {
  DataCollectionRequestDetails,
  DataCollectionRequestTemplateVersion,
} from "../../../../../api/types/dataCollectionTypes";
import type { ObjectAccessCategory } from "../../../../../api/types/objectTypes";
import InlinePropertyBox from "../../../../common/InlinePropertyBox";
import {
  getDefaultDataRequestName,
  getTemplateSettingsProperties,
  reportingDatePickerViewsMap,
} from "./dataRequestDialogHelper";
import { formToUpdateRequestPayload, getInitialEditDataRequestForm, validateForm } from "./editDataRequestForm";

interface Props {
  editedDataRequest: DataCollectionRequestDetails;
  requestTemplate: DataCollectionRequestTemplateVersion;
  categories: ObjectAccessCategory[];
  onClose: () => void;
  onSaved: (dataRequest: DataCollectionRequestDetails) => void;
}

const updateDataCollectionRequest = withErrorHandling(adminApi.updateDataCollectionRequest);

const EditDataRequestDialog = ({ editedDataRequest, requestTemplate, categories, onClose, onSaved }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(() => getInitialEditDataRequestForm(editedDataRequest));
  const [minReportingDate] = useState(() => subYears(new Date(), 10));

  const handleUpdate = async () => {
    const payload = formToUpdateRequestPayload(form);
    if (!payload) {
      return;
    }

    setSaving(true);
    const [resp, error] = await updateDataCollectionRequest(defined(editedDataRequest?.id), payload);
    setSaving(false);

    if (error) {
      logError(error, "[EditDataRequestDialog] updateDataCollectionRequest");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Data request updated successfully");
    onSaved(resp);
  };

  const handleNameChange = (name: string) => {
    setForm((prev) => ({ ...prev, name, touchedFields: [...prev.touchedFields, "name"] }));
  };

  const handleReportingDateChange = (date: Date | null) => {
    setForm((prev) => ({
      ...prev,
      reportingDate: date ? getLastDateOfReportingPeriod(date, requestTemplate.reportingPeriod) : date,
      name: form.touchedFields.includes("name") ? prev.name : getDefaultDataRequestName(requestTemplate, date),
      touchedFields: [...prev.touchedFields, "reportingDate"],
    }));
  };

  const handleDueDateChange = (date: Date | null) => {
    setForm((prev) => ({ ...prev, dueDate: date, touchedFields: [...prev.touchedFields, "dueDate"] }));
  };

  const { isFormValid, validationErrors } = validateForm(form);

  const reportingDatePickerViews = reportingDatePickerViewsMap[requestTemplate.reportingPeriod];

  const defaultReportingDatePickerView = reportingDatePickerViews?.includes("month") ? "month" : undefined;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Data Request</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={3}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="subtitle2" color="secondary">
                Request Template:
              </Typography>
              <Typography variant="subtitle2">{editedDataRequest.templateName}</Typography>
            </Stack>

            <InlinePropertyBox
              title="Template Settings"
              properties={getTemplateSettingsProperties(requestTemplate, categories)}
            />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="subtitle2" color="secondary">
              Scenario:
            </Typography>
            <Typography variant="subtitle2">{editedDataRequest.scenarioName}</Typography>
          </Stack>

          <Stack direction="row" spacing={3}>
            <DatePicker
              value={form.reportingDate}
              onChange={handleReportingDateChange}
              disabled={editedDataRequest.status !== "Draft"}
              views={reportingDatePickerViews}
              openTo={defaultReportingDatePickerView}
              minDate={minReportingDate}
              shouldDisableMonth={
                requestTemplate.reportingPeriod === "Quarter" ? (date) => date.getMonth() % 3 !== 2 : undefined
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  label: "Reporting Date",
                  error: !!validationErrors.reportingDate,
                  helperText: validationErrors.reportingDate,
                },
              }}
            />

            <DatePicker
              disablePast
              value={form.dueDate}
              onChange={handleDueDateChange}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  label: "Due Date",
                  error: !!validationErrors.dueDate,
                  helperText: validationErrors.reportingDate,
                },
              }}
            />
          </Stack>

          <TextField
            fullWidth
            label="Name"
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            error={!!validationErrors.name}
            helperText={validationErrors.name}
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={saving} onClick={handleUpdate} disabled={!isFormValid}>
          Save & update submissions
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default EditDataRequestDialog;
